import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Flex, Button, Radio } from "@chakra-ui/react";
import Modal from "src/components/Modal";
import { H3, BodyText2, Subtitle } from "src/atoms/typography";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { usePurchaseContext } from "src/modules/purchase/PurchaseContext";
import { useParentStation } from "src/modules/parent-station/ParentStation/Context";
import { useBabyStation } from "src/modules/baby-station/BabyStation/Context";
import { getMotionSensitivity } from "src/modules/baby-station/duck/selectors";
import { changeMotionSensitivity } from "src/modules/baby-station/duck/actions";
import motionSensitivity, {
  motionStringKeys
} from "./motionSensitivityOptions";

export default function MotionDetectionSettings({
  isOpen,
  onClose,
  remoteMotionSensitivity
}) {
  const parentStation = useParentStation();
  const babyStation = useBabyStation();
  const purchase = usePurchaseContext();

  const { handleMotionSensitivityChange } = parentStation || {};
  const { changeMotionDetectionSensitivity } = babyStation || {};
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isFreeVersion = useSelector(isFreemium);

  const storedSensitivity = useSelector(getMotionSensitivity);
  const [sensitivity, setSensitivity] = useState(storedSensitivity);
  const options = Object.keys(motionSensitivity);

  useEffect(() => {
    if (isFreeVersion) {
      dispatch(changeMotionSensitivity("MD_OFF"));
    }
  }, [isFreeVersion, dispatch, sensitivity]);

  useEffect(() => {
    if (parentStation && remoteMotionSensitivity) {
      setSensitivity(remoteMotionSensitivity);
      dispatch(changeMotionSensitivity(remoteMotionSensitivity));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteMotionSensitivity]);

  useEffect(() => {
    if (isOpen && storedSensitivity !== sensitivity) {
      setSensitivity(storedSensitivity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, storedSensitivity]);

  function handleSave({ sendMessage }) {
    dispatch(changeMotionSensitivity(sensitivity));
    if (parentStation && sendMessage) {
      handleMotionSensitivityChange(sensitivity);
    }
    if (babyStation && sendMessage) {
      changeMotionDetectionSensitivity(sensitivity);
    }
    onClose();
  }

  return (
    <Modal isOpen={isOpen}>
      <Box width="280px">
        <Box pt="24px" px="24px">
          <Flex flexDirection="column">
            <H3 color="highEmphasis" mb="22px">
              {t("motionDetection.heading")}
            </H3>
            {options.map((option) => {
              return (
                <Flex
                  key={option}
                  as="label"
                  mb="8px"
                  alignItems="center"
                  sx={{
                    "&:last-of-type": {
                      mb: "0px"
                    }
                  }}
                >
                  <Radio
                    value={option}
                    isChecked={sensitivity === option}
                    onChange={() => {
                      if (isFreeVersion) {
                        purchase.open({ content: "everyMove" });
                        onClose();
                      } else setSensitivity(option);
                    }}
                  />
                  <Subtitle ml="15px" color="highEmphasis">
                    {t(motionStringKeys[option])}
                  </Subtitle>
                </Flex>
              );
            })}
          </Flex>
          <BodyText2 mt="22px" mb="16px" color="mediumEmphasis">
            {t("motionDetection.description")}
          </BodyText2>
        </Box>
        <Flex mt="22px" pr="8px" pb="8px" justifyContent="flex-end">
          <Button
            variant="flatPrimary"
            onClick={onClose}
            minWidth="80px"
            mr="8px"
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="flatPrimary"
            onClick={() => handleSave({ sendMessage: true })}
            minWidth="80px"
          >
            {t("buttons.ok")}
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
}
